<script setup lang="ts">
import type { AcContactData } from "~/types/crm"
import { ref, watch } from 'vue'

const props = defineProps<{
    isVisible: boolean
    isEditing?: boolean
    existingContact?: AcContactData
    modalTitle?: string
}>()

const emits = defineEmits<{
    closed: []
    contactSaved: [contact: AcContactData]
}>()

// Form data
const contactForm = ref<AcContactData>({
    title: '',
    emailAddress: '',
    phoneNumber: '',
    address: '',
    website: ''
})

// Initialize form with existing contact data if editing
watch(() => props.existingContact, (newContact) => {
    if (newContact && props.isEditing) {
        contactForm.value = {
            title: newContact.title || '',
            emailAddress: newContact.emailAddress || '',
            phoneNumber: newContact.phoneNumber || '',
            address: newContact.address || '',
            website: newContact.website || ''
        }
    }
}, { immediate: true })

// Reset form when modal closes
watch(() => props.isVisible, (isVisible) => {
    if (!isVisible && !props.isEditing) {
        resetForm()
    }
})

const resetForm = () => {
    contactForm.value = {
        title: '',
        emailAddress: '',
        phoneNumber: '',
        address: '',
        website: ''
    }
}




const saveContact = () => {
    // Basic validation - title is required
    if (!contactForm.value.title.trim()) {
        return
    }
    
    // Create a clean contact object (remove empty strings)
    const cleanContact: AcContactData = {
        title: contactForm.value.title.trim()
    }
    
    if (contactForm.value.emailAddress?.trim()) {
        cleanContact.emailAddress = contactForm.value.emailAddress.trim()
    }
    
    if (contactForm.value.phoneNumber?.trim()) {
        cleanContact.phoneNumber = contactForm.value.phoneNumber.trim()
    }
    
    if (contactForm.value.address?.trim()) {
        cleanContact.address = contactForm.value.address.trim()
    }
    
    if (contactForm.value.website?.trim()) {
        cleanContact.website = contactForm.value.website.trim()
    }
    
    emits('contactSaved', cleanContact)
    
    if (!props.isEditing) {
        resetForm()
    }
}

</script>

<template>
   
                <form @submit.prevent="saveContact" class="ac-main-form !border-none">
                    <!-- Name/Title Field -->
                    <div class="ac-form-control">
                        <label for="contactTitle">İsim / Unvan *</label>
                        <input 
                            id="contactTitle"
                            v-model="contactForm.title"
                            type="text" 
                            required
                            class="w-full"
                            placeholder="Kişi adı veya şirket unvanı"
                        >
                    </div>

                    <!-- Email Field -->
                    <div class="ac-form-control">
                        <label for="contactEmail">E-posta Adresi</label>
                        <input 
                            id="contactEmail"
                            v-model="contactForm.emailAddress"
                            type="email" 
                            class="w-full"
                            placeholder="ornek@email.com"
                        >
                    </div>

                    <!-- Phone Field -->
                    <div class="ac-form-control">
                        <label for="contactPhone">Telefon Numarası</label>
                        <input 
                            id="contactPhone"
                            v-model="contactForm.phoneNumber"
                            type="tel" 
                            class="w-full"
                            placeholder="+90 555 123 45 67"
                        >
                    </div>

                    <!-- Address Field -->
                    <div class="ac-form-control">
                        <label for="contactAddress">Adres</label>
                        <textarea 
                            id="contactAddress"
                            v-model="contactForm.address"
                            class="w-full"
                            rows="3"
                            placeholder="Tam adres bilgisi"
                        ></textarea>
                    </div>

                    <!-- Website Field -->
                    <div class="ac-form-control">
                        <label for="contactWebsite">Website</label>
                        <input 
                            id="contactWebsite"
                            v-model="contactForm.website"
                            type="url" 
                            class="w-full"
                            placeholder="https://www.example.com"
                        >
                    </div>

                    <!-- Form Actions -->
                    <div class="form-actions">
                        <button 
                            type="button" 
                            @click="emits('closed')"
                            class="ac-secondary-button"
                        >
                            İptal
                        </button>
                        <button 
                            type="submit" 
                            class="ac-primary-button"
                        >
                            {{ isEditing ? 'Güncelle' : 'Kaydet' }}
                        </button>
                    </div>
                </form>

</template>

<style scoped>
.form-actions {
    display: flex;
    justify-content: center;
    gap: 1.5rem;
    margin-top: 3rem;
    padding-top: 2rem;
}

.form-actions button {
    min-width: 10rem;
    padding: 1rem 2rem;
    font-size: 1.4rem;
    font-weight: 500;
}

/* Override modal height for mobile */
@media screen and (max-width: 800px) {
    :deep(.ac-modal) {
        height: 80vh !important;
        overflow-y: auto;
    }
}
</style>
